import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { Shield, Lock, Database, CheckCircle, AlertTriangle } from "lucide-react";

const Security = () => {
  // Set meta tags for Security page

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="Security & Data Protection Policy | Frayze Technologies Inc."
        description="Frayze Technologies Inc. security and data protection policy. Learn how we protect your data with SOC 2-certified platforms, encryption, and industry-leading security standards."
        keywords="Frayze security policy, data protection Thunder Bay, SOC 2 compliance, GDPR compliance, cybersecurity, data encryption, secure CRM"
        ogTitle="Security & Data Protection Policy | Frayze Technologies Inc."
        ogDescription="At Frayze Technologies Inc., the security and privacy of our clients' data is central to everything we do. Learn about our security practices and certifications."
        twitterTitle="Security & Data Protection Policy | Frayze Technologies Inc."
        twitterDescription="At Frayze Technologies Inc., the security and privacy of our clients' data is central to everything we do."
        canonical="https://frayze.ca/security/"
        robots="noindex, follow"
      />
      <main className="container mx-auto px-4 py-16 pt-40 md:pt-40">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Security & Data Protection Policy</h1>
            <p className="text-xl text-muted-foreground mb-2">Frayze Technologies Inc.</p>
            <p className="text-sm text-muted-foreground">Last updated: January 2026</p>
            <p className="text-sm text-muted-foreground">Location: 202 Franklin St. North, Thunder Bay, Ontario</p>
          </div>

          {/* Introduction */}
          <div className="prose prose-gray dark:prose-invert prose-lg max-w-none mb-12">
            <p className="text-lg leading-relaxed">
              At Frayze Technologies Inc., the security and privacy of our clients' data is central to everything we do.
              We build, manage, and protect growth systems that connect marketing, sales, and operations across secure,
              compliant platforms.
            </p>
          </div>

          {/* Our Approach Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6 text-primary" />
              <h2 className="text-3xl font-bold">Our Approach</h2>
            </div>

            <p className="text-muted-foreground mb-6 leading-relaxed">
              We partner exclusively with industry-leading, SOC 2–certified and GDPR-compliant platforms including:
            </p>

            <div className="grid gap-4 mb-6">
              {[
                { name: "GoHighLevel", desc: "CRM and automation", cert: "SOC 2 Type II" },
                { name: "Google Workspace", desc: "communications and file management", cert: "SOC 2, ISO 27001" },
                { name: "Notion", desc: "documentation and collaboration", cert: "SOC 2 Type II" },
                { name: "Stripe", desc: "payment processing", cert: "SOC 2, PCI DSS Level 1" },
                { name: "Netlify, AWS", desc: "hosting and infrastructure", cert: "SOC 2, ISO 27001" },
                { name: "OpenAI", desc: "AI processing", cert: "SOC 2 Type II" }
              ].map((platform, idx) => (
                <div key={idx} className="bg-card border rounded-lg p-4 hover:border-primary/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">{platform.name}</p>
                      <p className="text-sm text-muted-foreground">{platform.desc}</p>
                      <p className="text-xs text-primary mt-1">{platform.cert}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
              <p className="text-sm leading-relaxed">
                <strong className="text-foreground">Encryption Standards:</strong>{" "}
                <span className="text-muted-foreground">
                  All data is transmitted using TLS 1.2+ encryption and stored using AES-256 encryption standards.
                </span>
              </p>
            </div>
          </section>

          {/* Access Controls Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Database className="w-6 h-6 text-primary" />
              <h2 className="text-3xl font-bold">Access Controls</h2>
            </div>

            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      Access to systems is restricted to authorized team members only.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      Multi-Factor Authentication (MFA) is required across all administrative and client environments.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      User permissions follow the principle of least privilege.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Retention & Confidentiality Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">Data Retention & Confidentiality</h2>

            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      Client data is retained only as long as required to deliver services or meet legal obligations.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      Upon termination of service, client data is securely deleted or exported upon request.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-card border rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-foreground leading-relaxed">
                      Frayze does not sell, share, or monetize client data in any form.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Incident Response Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <AlertTriangle className="w-6 h-6 text-primary" />
              <h2 className="text-3xl font-bold">Incident Response</h2>
            </div>

            <div className="bg-card border rounded-lg p-6 space-y-4">
              <p className="text-foreground leading-relaxed">
                Frayze maintains continuous monitoring of key systems and partners with vendors that provide
                real-time threat detection and automated backup systems.
              </p>
              <p className="text-foreground leading-relaxed">
                In the unlikely event of a data breach, all affected parties will be notified in accordance with
                federal and provincial regulations.
              </p>
            </div>
          </section>

          {/* Insurance & Compliance Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">Insurance & Compliance</h2>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 space-y-4">
              <p className="text-foreground leading-relaxed">
                Frayze carries commercial liability, professional (E&O), and cyber liability insurance.
              </p>
              <p className="text-foreground leading-relaxed">
                Our operations align with SOC 2, GDPR, and Canadian privacy frameworks.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6">Contact</h2>

            <div className="bg-muted/50 rounded-lg p-6">
              <p className="text-foreground mb-4">
                For questions or security inquiries, contact:
              </p>
              <div className="space-y-2">
                <p className="text-foreground">
                  Email: <a href="mailto:security@frayze.ca" className="text-primary hover:underline">security@frayze.ca</a>
                </p>
                <p className="text-foreground">
                  Address: Frayze Technologies Inc., 202 Franklin St. North, Thunder Bay, Ontario
                </p>
                <p className="text-foreground">
                  Web: <a href="https://frayze.ca" className="text-primary hover:underline">https://frayze.ca</a>
                </p>
              </div>
            </div>
          </section>

          {/* Footer Info */}
          <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground">
            <p><strong>Last Updated:</strong> January 2026</p>
            <p><strong>Document ID:</strong> SECURITY-2026-001</p>
            <p><strong>Compliance:</strong> SOC 2, GDPR, PIPEDA</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Security;

